import { useAppSelector } from "@jidlomat/terminal-core/hooks/utilsHooks/storeHooks";
import { selectActiveLocalTransaction } from "@jidlomat/terminal-core/slices/transactionSlice";
import { getFridgeId } from "@jidlomat/terminal-core/utils/browser/localStorage";
import { getShortenedFridgeId } from "@jidlomat/terminal-core/utils/utils";
import { BasicModal } from "@jidlomat/terminal-ui/components/global/modals/BasicModal";
import { Box, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import type { PropsWithChildren, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ErrorModalContent } from "../components/global/modals/modalsContents/ErrorModalContent";
import { useRecoveryReload } from "./useRecoveryReload";

// A part of the page that throws while it is drawn takes down only itself: the boundary around it
// shows its fallback for the rest of the page load and gets the page reloaded, which rebuilds from the
// backend the state that crashed. The fallbacks are components of their own, defined once: a component
// created while the page renders would be a new one with every change of the store, and each would
// start its recovery anew.

/** Where a boundary sits: the tag of its event in Sentry, and the reason of its reload. */
type BoundaryName = "root" | "flow" | "menu";

/**
 * The screen of the connection error, for a crash that leaves the kiosk unable to sell. Without what
 * the connection error logs and tracks: that stays the record of a lost connection. It needs no
 * provider of the app, because the root boundary shows it when one of them is what threw: the fridge
 * id comes from storage.
 */
const ErrorScreen = ({ boundary }: { boundary: "root" | "flow" }) => {
	useRecoveryReload(`render error: ${boundary}`, true);
	return (
		<BasicModal
			open={true}
			fullHeight={true}
			closable={false}
			modalContent={<ErrorModalContent fridgeId={getShortenedFridgeId(getFridgeId())} />}
		/>
	);
};

/**
 * In place of the menu, a notice that the fridge still sells: the drawer sells without the menu, and a
 * blank panel next to it would read as "no food". The reload waits while an order or a local refill is
 * in the drawer, running or showing its summary, so that it takes neither away from the customer. A
 * remote refill never shows the menu. Styled like the menu's own empty state, with the theme's colours
 * named rather than read, so that it cannot throw where the menu just did.
 */
const MenuUnavailable = () => {
	const { t } = useTranslation();
	const drawerEmpty = useAppSelector((state) => selectActiveLocalTransaction(state) === null);
	useRecoveryReload("render error: menu", drawerEmpty);
	return (
		<Box
			data-testid="menu-unavailable"
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				textAlign: "center",
				pt: 2.5,
				px: 4,
			}}
		>
			<Typography variant="promoHeader" color="copper.main">
				{t("dishListPage.unavailable.title")}
			</Typography>
			<Typography variant="mealSub" color="common.white" sx={{ mt: 1 }}>
				{t("dishListPage.unavailable.body")}
			</Typography>
		</Box>
	);
};

type RecoveringErrorBoundaryProps = PropsWithChildren<{ name: BoundaryName; fallback: ReactElement }>;

/** Sentry's boundary: it reports what it catches, once, tagged with where it sits. */
const RecoveringErrorBoundary = ({ name, fallback, children }: RecoveringErrorBoundaryProps) => (
	<Sentry.ErrorBoundary
		beforeCapture={(scope) => {
			scope.setTag("errorBoundary", name);
		}}
		fallback={fallback}
	>
		{children}
	</Sentry.ErrorBoundary>
);

/** Around the whole page, its providers included: the error screen, and a reload at once. */
export const RootErrorBoundary = ({ children }: PropsWithChildren) => (
	<RecoveringErrorBoundary name="root" fallback={<ErrorScreen boundary="root" />}>
		{children}
	</RecoveringErrorBoundary>
);

/**
 * Around the screen of the flow, the order drawer of either flow. Without it the kiosk cannot sell:
 * the error screen, and a reload at once.
 */
export const FlowErrorBoundary = ({ children }: PropsWithChildren) => (
	<RecoveringErrorBoundary name="flow" fallback={<ErrorScreen boundary="flow" />}>
		{children}
	</RecoveringErrorBoundary>
);

/** Around the menu: a notice in its place while the drawer goes on selling, and a reload once it is empty. */
export const MenuErrorBoundary = ({ children }: PropsWithChildren) => (
	<RecoveringErrorBoundary name="menu" fallback={<MenuUnavailable />}>
		{children}
	</RecoveringErrorBoundary>
);
